export const javascriptCodingQuizData = [
    {
        module: "Day 1 – JavaScript Basics: Console Output & Comments",
        questions: [
            {
                id: "jsc-1-1",
                type: "coding",
                question: "Print 'Hello, JavaScript!' to the console.",
                description: "Use console.log() to output the exact text: Hello, JavaScript!",
                hint: "console.log() is the JavaScript function for printing to the console. Put the text inside quotes.",
                starterCode: `// Print Hello, JavaScript! to the console\n`,
                expectedOutput: "Hello, JavaScript!",
                testCases: [{ input: "", expectedOutput: "Hello, JavaScript!", description: "Should print Hello, JavaScript!" }],
                solution: `console.log("Hello, JavaScript!");`,
                ariaLabel: "Coding challenge one. Print Hello, JavaScript! to the console using console.log(). Type your JavaScript code in the text area below.",
            },
            {
                id: "jsc-1-2",
                type: "coding",
                question: "Print your name to the console using a variable.",
                description: "Declare a variable called name, assign your name to it, then print it using console.log().",
                hint: "Declare with: const name = 'YourName'; then use console.log(name);",
                starterCode: `// Declare a variable called name and print it\n`,
                expectedOutput: "Riya",
                testCases: [{ input: "", expectedOutput: "Riya", description: "Should print a name" }],
                solution: `const name = 'Riya';\nconsole.log(name);`,
                ariaLabel: "Coding challenge two. Declare a variable called name and print it with console.log. Type your code in the text area below.",
            },
        ],
    },
    {
        module: "Day 2 – Variables, Data Types & Operators",
        questions: [
            {
                id: "jsc-2-1",
                type: "coding",
                question: "Declare a let variable called age with value 25, then print it.",
                description: "Use let to declare the variable age, assign the value 25, and print it with console.log().",
                hint: "let age = 25; then console.log(age);",
                starterCode: `// Declare age using let and print it\n`,
                expectedOutput: "25",
                testCases: [{ input: "", expectedOutput: "25", description: "Should print 25" }],
                solution: `let age = 25;\nconsole.log(age);`,
                ariaLabel: "Coding challenge one. Declare a variable called age using let with value 25 and print it. Type your code below.",
            },
            {
                id: "jsc-2-2",
                type: "coding",
                question: "Print the result of 10 + 5 * 2 to the console.",
                description: "Use console.log() to print the arithmetic expression 10 + 5 multiplied by 2. Remember operator precedence.",
                hint: "console.log(10 + 5 * 2); Multiplication happens before addition so the result is 20.",
                starterCode: `// Print the result of 10 + 5 * 2\n`,
                expectedOutput: "20",
                testCases: [{ input: "", expectedOutput: "20", description: "Should print 20" }],
                solution: `console.log(10 + 5 * 2);`,
                ariaLabel: "Coding challenge two. Calculate 10 plus 5 times 2 and print the result. Type your code below.",
            },
        ],
    },
    {
        module: "Day 3 – Functions & Scope",
        questions: [
            {
                id: "jsc-3-1",
                type: "coding",
                question: "Write a function called greet that takes a name parameter and returns 'Hello, [name]!'",
                description: "Define a function called greet with one parameter called name. It should return the greeting string. Then call it with the name 'Riya' and print the result.",
                hint: "function greet(name) { return 'Hello, ' + name + '!'; } then console.log(greet('Riya'));",
                starterCode: `// Write the greet function\nfunction greet(name) {\n  // return the greeting here\n}\n\nconsole.log(greet('Riya'));`,
                expectedOutput: "Hello, Riya!",
                testCases: [{ input: "Riya", expectedOutput: "Hello, Riya!", description: "Should return Hello, Riya!" }],
                solution: `function greet(name) {\n  return 'Hello, ' + name + '!';\n}\nconsole.log(greet('Riya'));`,
                ariaLabel: "Coding challenge one. Write a function called greet that takes a name and returns a greeting string. Type your code in the text area below.",
            },
            {
                id: "jsc-3-2",
                type: "coding",
                question: "Write an arrow function called double that takes a number and returns its double.",
                description: "Use arrow function syntax to write a function called double. It should take one number parameter and return that number multiplied by 2. Call it with 7 and print the result.",
                hint: "const double = (num) => num * 2; then console.log(double(7));",
                starterCode: `// Write double as an arrow function\nconst double = (num) => {\n  // return double of num\n};\n\nconsole.log(double(7));`,
                expectedOutput: "14",
                testCases: [{ input: "7", expectedOutput: "14", description: "Should print 14" }],
                solution: `const double = (num) => num * 2;\nconsole.log(double(7));`,
                ariaLabel: "Coding challenge two. Write an arrow function called double that returns twice the given number. Type your code below.",
            },
        ],
    },
    {
        module: "Day 4 – Arrays & Objects",
        questions: [
            {
                id: "jsc-4-1",
                type: "coding",
                question: "Create an array of 3 fruits and print the second fruit.",
                description: "Declare an array called fruits with three fruit names. Then print the second fruit using its index (remember arrays start at index 0).",
                hint: "const fruits = ['apple', 'banana', 'cherry']; console.log(fruits[1]);",
                starterCode: `// Create the fruits array and print the second one\n`,
                expectedOutput: "banana",
                testCases: [{ input: "", expectedOutput: "banana", description: "Should print the second fruit" }],
                solution: `const fruits = ['apple', 'banana', 'cherry'];\nconsole.log(fruits[1]);`,
                ariaLabel: "Coding challenge one. Create an array of three fruits and print the second one using index 1. Type your code below.",
            },
            {
                id: "jsc-4-2",
                type: "coding",
                question: "Create a person object with name and age properties, then print the name.",
                description: "Declare an object called person with two properties: name set to 'Riya' and age set to 25. Then print the name property.",
                hint: "const person = { name: 'Riya', age: 25 }; console.log(person.name);",
                starterCode: `// Create the person object and print the name\n`,
                expectedOutput: "Riya",
                testCases: [{ input: "", expectedOutput: "Riya", description: "Should print Riya" }],
                solution: `const person = { name: 'Riya', age: 25 };\nconsole.log(person.name);`,
                ariaLabel: "Coding challenge two. Create an object called person with name and age and print the name. Type your code below.",
            },
        ],
    },
    {
        module: "Day 5 – Control Flow: Conditions & Loops",
        questions: [
            {
                id: "jsc-5-1",
                type: "coding",
                question: "Write code that prints 'Even' if a number is even, or 'Odd' if it is odd. Use number 8.",
                description: "Store the value 8 in a variable. Use an if-else statement with the modulo operator to check if it is even or odd, then print the correct word.",
                hint: "let num = 8; if (num % 2 === 0) { console.log('Even'); } else { console.log('Odd'); }",
                starterCode: `// Check if the number is even or odd\nlet num = 8;\n`,
                expectedOutput: "Even",
                testCases: [{ input: "", expectedOutput: "Even", description: "Should print Even for 8" }],
                solution: `let num = 8;\nif (num % 2 === 0) {\n  console.log('Even');\n} else {\n  console.log('Odd');\n}`,
                ariaLabel: "Coding challenge one. Check if 8 is even or odd and print the result. Type your code below.",
            },
            {
                id: "jsc-5-2",
                type: "coding",
                question: "Use a for loop to print numbers from 1 to 5.",
                description: "Write a for loop that starts at 1, ends at 5, and prints each number on a new line using console.log().",
                hint: "for (let i = 1; i <= 5; i++) { console.log(i); }",
                starterCode: `// Use a for loop to print 1 to 5\n`,
                expectedOutput: "1\n2\n3\n4\n5",
                testCases: [{ input: "", expectedOutput: "1", description: "Should print 1 through 5" }],
                solution: `for (let i = 1; i <= 5; i++) {\n  console.log(i);\n}`,
                ariaLabel: "Coding challenge two. Use a for loop to print the numbers 1 through 5. Type your code below.",
            },
        ],
    },
    {
        module: "Day 6 – DOM Manipulation Concepts",
        questions: [
            {
                id: "jsc-6-1",
                type: "coding",
                question: "Write JavaScript that would select an element with id 'title' and change its text.",
                description: "Write code using document.querySelector to select an element with id 'title'. Set its textContent to 'Updated Title'. Note: this will not run in our simulator but tests your knowledge of DOM APIs.",
                hint: "const el = document.querySelector('#title'); el.textContent = 'Updated Title';",
                starterCode: `// Write DOM manipulation code\n// Select #title and change its text\n`,
                expectedOutput: "Success",
                testCases: [{ input: "", expectedOutput: "Success", description: "Code should demonstrate DOM selection" }],
                solution: `const el = document.querySelector('#title');\nel.textContent = 'Updated Title';\nconsole.log('Success');`,
                ariaLabel: "Coding challenge one. Write DOM manipulation code to select an element by ID and change its text. Type your code below.",
            },
            {
                id: "jsc-6-2",
                type: "coding",
                question: "Write code to change the background color of an element with class 'box' to blue.",
                description: "Write JavaScript code that selects an element with the CSS class 'box' and changes its background color to blue using the style property.",
                hint: "const box = document.querySelector('.box'); box.style.backgroundColor = 'blue';",
                starterCode: `// Write code to change the background color of .box\n`,
                expectedOutput: "Success",
                testCases: [{ input: "", expectedOutput: "Success", description: "Code should demonstrate style manipulation" }],
                solution: `const box = document.querySelector('.box');\nbox.style.backgroundColor = 'blue';\nconsole.log('Success');`,
                ariaLabel: "Coding challenge two. Write code to change the background color of an element with class box. Type your code below.",
            },
        ],
    },
    {
        module: "Day 7 – Events & Event Listeners",
        questions: [
            {
                id: "jsc-7-1",
                type: "coding",
                question: "Write code to add a click event listener to a button with id 'myBtn' that prints 'Button clicked!'",
                description: "Use document.querySelector to get the button, then addEventListener to attach a click handler that runs console.log('Button clicked!').",
                hint: "const btn = document.querySelector('#myBtn'); btn.addEventListener('click', () => console.log('Button clicked!'));",
                starterCode: `// Add click event listener to #myBtn\n`,
                expectedOutput: "Success",
                testCases: [{ input: "", expectedOutput: "Success", description: "Code should add a click event listener" }],
                solution: `const btn = document.querySelector('#myBtn');\nbtn.addEventListener('click', () => console.log('Button clicked!'));\nconsole.log('Success');`,
                ariaLabel: "Coding challenge one. Write code to add a click event listener to a button element. Type your code below.",
            },
            {
                id: "jsc-7-2",
                type: "coding",
                question: "Write a function that prevents a form from submitting and prints 'Form submitted safely!' instead.",
                description: "Write a handleSubmit function that takes an event parameter, calls event.preventDefault() to stop the form from reloading the page, then prints the success message.",
                hint: "function handleSubmit(event) { event.preventDefault(); console.log('Form submitted safely!'); }",
                starterCode: `// Write the handleSubmit function\nfunction handleSubmit(event) {\n  // prevent default and log message\n}\n\n// Test it (in real code this would be attached to a form)\nhandleSubmit({ preventDefault: () => {} });`,
                expectedOutput: "Form submitted safely!",
                testCases: [{ input: "", expectedOutput: "Form submitted safely!", description: "Should print Form submitted safely!" }],
                solution: `function handleSubmit(event) {\n  event.preventDefault();\n  console.log('Form submitted safely!');\n}\nhandleSubmit({ preventDefault: () => {} });`,
                ariaLabel: "Coding challenge two. Write a function that prevents default form submission and logs a message. Type your code below.",
            },
        ],
    },
    {
        module: "Day 8 – Asynchronous JavaScript & Promises",
        questions: [
            {
                id: "jsc-8-1",
                type: "coding",
                question: "Write an async function called getData that returns the string 'data loaded'.",
                description: "Write an async function called getData. Inside it, use await with a Promise that resolves after a simulated delay and returns 'data loaded'. Then call it and print the result.",
                hint: "async function getData() { const result = await Promise.resolve('data loaded'); return result; } getData().then(console.log);",
                starterCode: `// Write an async function called getData\nasync function getData() {\n  // return 'data loaded'\n}\n\ngetData().then(result => console.log(result));`,
                expectedOutput: "data loaded",
                testCases: [{ input: "", expectedOutput: "data loaded", description: "Should print data loaded" }],
                solution: `async function getData() {\n  const result = await Promise.resolve('data loaded');\n  return result;\n}\ngetData().then(result => console.log(result));`,
                ariaLabel: "Coding challenge one. Write an async function that returns a string using await. Type your code below.",
            },
            {
                id: "jsc-8-2",
                type: "coding",
                question: "Create a Promise that resolves with 'success' and print the result using .then().",
                description: "Create a new Promise using the Promise constructor. Inside the executor function, call resolve with the string 'success'. Then chain a .then() handler to print the resolved value.",
                hint: "new Promise((resolve) => resolve('success')).then(value => console.log(value));",
                starterCode: `// Create a Promise and handle its resolved value\n`,
                expectedOutput: "success",
                testCases: [{ input: "", expectedOutput: "success", description: "Should print success" }],
                solution: `new Promise((resolve) => resolve('success'))\n  .then(value => console.log(value));`,
                ariaLabel: "Coding challenge two. Create a Promise that resolves with success and print it using .then(). Type your code below.",
            },
        ],
    },
    {
        module: "Day 9 – Fetch API & JSON",
        questions: [
            {
                id: "jsc-9-1",
                type: "coding",
                question: "Convert a JavaScript object to JSON string and print it.",
                description: "Create an object called user with a name property set to 'Riya' and an age of 25. Convert it to a JSON string using JSON.stringify() and print it.",
                hint: "const user = { name: 'Riya', age: 25 }; console.log(JSON.stringify(user));",
                starterCode: `// Create user object and convert to JSON string\n`,
                expectedOutput: `{"name":"Riya","age":25}`,
                testCases: [{ input: "", expectedOutput: `{"name":"Riya","age":25}`, description: "Should print JSON string" }],
                solution: `const user = { name: 'Riya', age: 25 };\nconsole.log(JSON.stringify(user));`,
                ariaLabel: "Coding challenge one. Create a user object and convert it to a JSON string using JSON.stringify. Type your code below.",
            },
            {
                id: "jsc-9-2",
                type: "coding",
                question: "Parse a JSON string back into a JavaScript object and print the name property.",
                description: "Start with the JSON string '{\"name\":\"Alex\",\"age\":30}'. Use JSON.parse() to convert it into a JavaScript object, then print the name property.",
                hint: `const jsonStr = '{"name":"Alex","age":30}'; const obj = JSON.parse(jsonStr); console.log(obj.name);`,
                starterCode: `// Parse the JSON string and print the name\nconst jsonStr = '{"name":"Alex","age":30}';\n`,
                expectedOutput: "Alex",
                testCases: [{ input: "", expectedOutput: "Alex", description: "Should print Alex" }],
                solution: `const jsonStr = '{"name":"Alex","age":30}';\nconst obj = JSON.parse(jsonStr);\nconsole.log(obj.name);`,
                ariaLabel: "Coding challenge two. Parse a JSON string into an object and print the name property. Type your code below.",
            },
        ],
    },
    {
        module: "Day 10 – Final JavaScript Project Skills",
        questions: [
            {
                id: "jsc-10-1",
                type: "coding",
                question: "Write a function called calculateTotal that uses array reduce to sum an array of numbers.",
                description: "Write a function called calculateTotal that accepts an array of numbers and uses the .reduce() method to return their total sum. Test it with the array [10, 20, 30, 40].",
                hint: "function calculateTotal(nums) { return nums.reduce((sum, n) => sum + n, 0); } console.log(calculateTotal([10, 20, 30, 40]));",
                starterCode: `// Write calculateTotal using reduce\nfunction calculateTotal(nums) {\n  // use reduce to sum all numbers\n}\n\nconsole.log(calculateTotal([10, 20, 30, 40]));`,
                expectedOutput: "100",
                testCases: [{ input: "[10, 20, 30, 40]", expectedOutput: "100", description: "Should return 100" }],
                solution: `function calculateTotal(nums) {\n  return nums.reduce((sum, n) => sum + n, 0);\n}\nconsole.log(calculateTotal([10, 20, 30, 40]));`,
                ariaLabel: "Coding challenge one. Write a function that uses array reduce to sum all numbers in an array. Type your code below.",
            },
            {
                id: "jsc-10-2",
                type: "coding",
                question: "Use destructuring to extract name and age from a person object and print them.",
                description: "Create an object called person with a name of 'Sam' and an age of 22. Use object destructuring to extract both properties into variables, then print both values.",
                hint: "const person = { name: 'Sam', age: 22 }; const { name, age } = person; console.log(name); console.log(age);",
                starterCode: `// Use destructuring to extract name and age\nconst person = { name: 'Sam', age: 22 };\n`,
                expectedOutput: "Sam\n22",
                testCases: [{ input: "", expectedOutput: "Sam", description: "Should print Sam and 22" }],
                solution: `const person = { name: 'Sam', age: 22 };\nconst { name, age } = person;\nconsole.log(name);\nconsole.log(age);`,
                ariaLabel: "Coding challenge two. Use object destructuring to extract name and age from a person object and print them. Type your code below.",
            },
        ],
    },
];
